'use client';

import { useState, useEffect, useCallback } from 'react';

import type {
  PurchasesOffering,
  PurchasesPackage,
  CustomerInfo,
} from '@revenuecat/purchases-capacitor';
import { useAmplifyServiceContext } from '@scorenco/core';

import { purchasesService, SubscriptionStatus } from '../services/purchases';

export interface UsePurchasesReturn {
  // État de l'abonnement
  subscriptionStatus: SubscriptionStatus;
  isSubscribed: boolean;
  isPremium: boolean;

  // Offres disponibles
  offerings: PurchasesOffering | null;
  isLoading: boolean;
  error: string | null;

  // Actions
  purchasePackage: (packageToPurchase: PurchasesPackage) => Promise<boolean>;
  restorePurchases: () => Promise<boolean>;
  refreshSubscriptionStatus: () => Promise<void>;
}

export const usePurchases = (): UsePurchasesReturn => {
  const { user } = useAmplifyServiceContext();
  const [subscriptionStatus, setSubscriptionStatus] =
    useState<SubscriptionStatus>({
      isSubscribed: false,
      isPremium: false,
    });
  const [offerings, setOfferings] = useState<PurchasesOffering | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Réagir aux changements d'utilisateur (login/logout) pour synchroniser RevenueCat
  useEffect(() => {
    const appUserID = user?.info?.id ? String(user.info.id) : undefined;
    purchasesService.setAppUserId(appUserID);
  }, [user?.info?.id]);

  // Rafraîchir le statut d'abonnement
  const refreshSubscriptionStatus = useCallback(async () => {
    try {
      const status = await purchasesService.getSubscriptionStatus();
      setSubscriptionStatus(status);
    } catch (err) {
      setError(
        err instanceof Error
          ? err.message
          : 'Erreur lors du rafraîchissement du statut'
      );
      console.error('Failed to refresh subscription status:', err);
    }
  }, []);

  // Effectuer un achat
  const purchasePackage = useCallback(
    async (packageToPurchase: PurchasesPackage): Promise<boolean> => {
      try {
        setIsLoading(true);
        setError(null);

        const customerInfo =
          await purchasesService.purchasePackage(packageToPurchase);

        if (customerInfo) {
          // Mettre à jour le statut après l'achat
          await refreshSubscriptionStatus();
          return true;
        }

        return false;
      } catch (err) {
        setError(err instanceof Error ? err.message : "Erreur lors de l'achat");
        console.error('Purchase failed:', err);
        return false;
      } finally {
        setIsLoading(false);
      }
    },
    [refreshSubscriptionStatus]
  );

  // Restaurer les achats
  const restorePurchases = useCallback(async (): Promise<boolean> => {
    try {
      setIsLoading(true);
      setError(null);

      const customerInfo = await purchasesService.restorePurchases();

      if (customerInfo) {
        // Mettre à jour le statut après la restauration
        await refreshSubscriptionStatus();
        return true;
      }

      return false;
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Erreur lors de la restauration'
      );
      console.error('Restore purchases failed:', err);
      return false;
    } finally {
      setIsLoading(false);
    }
  }, [refreshSubscriptionStatus]);

  // Initialiser au montage du composant
  useEffect(() => {
    const appUserID = user?.info?.id ? String(user.info.id) : undefined;

    async function runInit() {
      try {
        await purchasesService.initialize(appUserID);
        await purchasesService.setAppUserId(appUserID);

        const [offeringsData, status] = await Promise.all([
          purchasesService.getOfferings(),
          purchasesService.getSubscriptionStatus(),
        ]);

        setOfferings(offeringsData);
        setSubscriptionStatus(status);
      } catch (err) {
        setError(
          err instanceof Error ? err.message : 'Une erreur est survenue'
        );
        console.error('Failed to initialize purchases:', err);
      } finally {
        setIsLoading(false);
      }
    }

    void runInit();
  }, []);

  // Écouter les changements d'abonnement
  useEffect(() => {
    const handleCustomerInfoUpdate = (customerInfo: CustomerInfo) => {
      const activeEntitlements = Object.values(
        customerInfo.entitlements?.active || {}
      );
      const isSubscribed = activeEntitlements.length > 0;
      const isPremium = isSubscribed;

      let currentPlan: string | undefined;
      let expirationDate: Date | undefined;

      if (isSubscribed) {
        const entitlement = activeEntitlements[0] as any;
        currentPlan = entitlement.productIdentifier;
        if (entitlement.expirationDate) {
          expirationDate = new Date(entitlement.expirationDate);
        }
      }

      setSubscriptionStatus({
        isSubscribed,
        isPremium,
        currentPlan,
        expirationDate,
      });
    };

    // Ajouter l'écouteur
    purchasesService.addCustomerInfoUpdateListener(handleCustomerInfoUpdate);

    // Nettoyer l'écouteur au démontage
    return () => {
      purchasesService.removeCustomerInfoUpdateListener(
        handleCustomerInfoUpdate
      );
    };
  }, []);

  return {
    subscriptionStatus,
    isSubscribed: subscriptionStatus.isSubscribed,
    isPremium: subscriptionStatus.isPremium,
    offerings,
    isLoading,
    error,
    purchasePackage,
    restorePurchases,
    refreshSubscriptionStatus,
  };
};
